import React, {forwardRef, memo, useEffect} from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'

/* Utils */
import {hasIntersectionObserver} from '../../common/dom/domUtils'
import {debounce} from '../../common/performance/performanceUtils'
import {yieldToMain} from '../../utils/performance'

/* Styles */
import s from './CssCarousel.scss'

const CssCarousel = forwardRef(({
  className,
  navigationWrapperClass,
  carouselSnapperClassName,
  badgeContainer,
  children,
  prefix,
  thumbnail,
  activeSlideIndex,
  onSlideChange,
  navigationType,
  arrowsEnable,
  arrowsClassName,
  contentClassName
}, ref) => {

  useEffect(() => {
    calculateCarouselDots()
  }, [])

  async function calculateCarouselDots() {
    await yieldToMain()
    const elements = document.querySelectorAll(`.${prefix}-slideItem`);
    const root = document.querySelector(`#${prefix}-carouselContainer`);

    const interSectionObserverOptions = {
      root: root,
      rootMargin: '0px',
      threshold: 0.90,
    };

    const interSectionObserverCallback = debounce((entries) => {
      entries.forEach(async(entry) => {
        await yieldToMain()
        if (entry.isIntersecting && entry.intersectionRatio > 0.80) {
          const intersectingItem = entry.target.id;
          const intersectingItemIndex = intersectingItem ? extractNumber(intersectingItem) : 0;
          await yieldToMain()
          onSlideChange(parseInt(intersectingItemIndex))
        }
      });
    }, 100);

    if (hasIntersectionObserver) {
      const observer = new IntersectionObserver(interSectionObserverCallback, interSectionObserverOptions);
      elements.forEach(async(target) => {
        await yieldToMain()
        observer.observe(target);
      });
    }
  }

  function extractNumber(text) {
    const match = text.match(/\d+/);
    return match ? parseInt(match[0], 10) : null;
  }

  const navigationClass = cx({
    [s.carousel__navigation_button__dot]: navigationType === 'dot',
    [s.carousel__navigation_button__rectangle]: navigationType === 'rectangle'
  })

  const carouselContent = () => {
    return (
      <>
        <ol className={cx(s.carousel__viewport, contentClassName)}>
          {badgeContainer && badgeContainer}
          <div className={s.carousel__snapper}>
          </div>
          {
            children?.map((item, index) => {
              return (
                <li
                  key={index}
                  id={`${prefix}-carousel__slide${index}`}
                  tabIndex={index}
                  className={cx(s.carousel__slide, `${prefix}-slideItem`)}>
                  <div className={cx(s.carousel__snapper, carouselSnapperClassName)}>
                    {item}
                  </div>
                </li>
              )
            })
          }
        </ol>
        {arrowsEnable && carouselArrows()}
      </>
    )
  }

  const carouselArrows = () => {
    return (
      <div className={cx(s.carousel__arrow, arrowsClassName)}>
        <div
          className={s.carousel__prev}
          onClick={() => onSlideChange(activeSlideIndex === 0 ? children?.length - 1 : activeSlideIndex - 1)}/>
        <div
          className={s.carousel__next}
          onClick={() => onSlideChange(children?.length - 1 === activeSlideIndex ? 0 : activeSlideIndex + 1)}/>
      </div>
    )
  }

  const carouselNavigation = () => {
    return (
      <div className={cx(s.carousel__navigation, navigationWrapperClass)}>
        <ol className={s.carousel__navigation_list}>
          {
            children?.length > 1 && children?.map((data, index) => {
              return (
                <li
                  key={index}
                  className={cx(s.carousel__navigation_item, 'dotsItem')}
                  id={`${prefix}-carousel_dots${index}`}>
                  <span
                    id={`${prefix}-carousel__dot_item${index}`}
                    className={cx(navigationClass, {[s.carousel__navigation_button__video]: data?.props?.children?.props?.video}, {[s.active]: activeSlideIndex === index})}/>
                </li>
              )
            })
          }
        </ol>
      </div>
    )
  }

  return (
    <div ref={ref} className={cx(s.carousel, className)} id={`${prefix}-carouselContainer`}>
      {carouselContent()}
      {thumbnail ? thumbnail : carouselNavigation()}
    </div>
  )
});

CssCarousel.propTypes = {
  className: PropTypes.string,
  prefix: PropTypes.string.isRequired,
  children: PropTypes.array.isRequired,
  thumbnail: PropTypes.element,
  arrowsEnable: PropTypes.bool,
  navigationType: PropTypes.oneOf(['dot', 'rectangle']),
  navigationWrapperClass: PropTypes.string,
  arrowsClassName: PropTypes.string,
  contentClassName: PropTypes.string
}

CssCarousel.defaultProps = {
  navigationType: 'dot',
  arrowsEnable: false,
};

export default memo(CssCarousel);
